import React, { useState, useEffect, useCallback } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  getDoc,
} from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import {
  FaSearch,
  FaChevronDown,
  FaChevronRight,
  FaBell,
  FaClock,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Add some CSS for the accordion
const accordionStyles = `
    .accordion-button:focus {
        box-shadow: none;
    }
    .accordion-arrow {
        transition: transform 0.2s ease;
    }
    .accordion-button[aria-expanded="true"] .accordion-arrow {
        transform: rotate(180deg);
    }
    .accordion-button {
        color: #495057 !important;
    }
    .accordion-button:hover {
        background-color: #f8f9fa;
    }
`;

const NotificationsPage = () => {
  const [traces, setTraces] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [expandedTraces, setExpandedTraces] = useState(new Set());
  const [expandedHistoryItems, setExpandedHistoryItems] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  // Compute filtered traces based on search input
  const filteredTraces = traces.filter((trace) => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase();
    const orgIdMatch = trace.organizationId
      ?.toLowerCase()
      .includes(searchLower);
    const orgNameMatch = trace.organizationName
      ?.toLowerCase()
      .includes(searchLower);
    const loanIdMatch = trace.loanId?.toLowerCase().includes(searchLower);
    const traceIdMatch = trace.id?.toLowerCase().includes(searchLower);

    // Also search in history messages
    const historyMatch = trace.history?.some((item) =>
      item.message?.toLowerCase().includes(searchLower)
    );

    return (
      orgIdMatch || orgNameMatch || loanIdMatch || traceIdMatch || historyMatch
    );
  });

  const fetchOrganizations = useCallback(async (organizationIds) => {
    const organizationsMap = {};

    if (!organizationIds || organizationIds.length === 0) {
      return organizationsMap;
    }

    try {
      // Fetch each organization by ID
      const fetchPromises = organizationIds.map(async (orgId) => {
        try {
          const orgRef = doc(firestoreProd, "organizations", orgId);
          const orgSnapshot = await getDoc(orgRef);

          if (orgSnapshot.exists()) {
            const orgData = orgSnapshot.data();
            return { id: orgId, name: orgData.name || orgId };
          }
          return { id: orgId, name: null };
        } catch (error) {
          console.warn(`Error fetching organization ${orgId}:`, error);
          return { id: orgId, name: null };
        }
      });

      const orgResults = await Promise.all(fetchPromises);

      // Build map of organizationId -> organizationName
      orgResults.forEach(({ id, name }) => {
        organizationsMap[id] = name;
      });

      console.log("Organizations map:", organizationsMap);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }

    return organizationsMap;
  }, []);

  const fetchNotificationTraces = useCallback(async () => {
    if (!firestoreProd) {
      toast.error("Firebase is not initialized");
      return;
    }

    setIsLoading(true);
    setError(null);
    setTraces([]);
    setHasLoaded(true);

    try {
      const tracesRef = collection(firestoreProd, "notification-traces");
      const q = query(tracesRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        toast.info("No notification traces found");
        setTraces([]);
        return;
      }

      const tracesList = [];
      const organizationIds = new Set();

      for (const docSnapshot of querySnapshot.docs) {
        const traceData = docSnapshot.data();

        // Debug: Log first trace to see data structure
        if (tracesList.length === 0) {
          console.log("First trace data:", traceData);
          console.log("organizationId:", traceData.organizationId);
          console.log("loanId:", traceData.loanId);
        }

        tracesList.push({
          id: docSnapshot.id,
          ...traceData,
        });

        // Collect unique organization IDs
        if (traceData.organizationId) {
          organizationIds.add(traceData.organizationId);
        }
      }

      // Fetch all organizations
      console.log("Fetching organizations:", Array.from(organizationIds));
      const organizationsMap = await fetchOrganizations(
        Array.from(organizationIds)
      );

      // Add organization names to traces
      const tracesWithOrgNames = tracesList.map((trace) => ({
        ...trace,
        organizationName: trace.organizationId
          ? organizationsMap[trace.organizationId] || null
          : null,
      }));

      setTraces(tracesWithOrgNames);
      toast.success(`Loaded ${tracesWithOrgNames.length} notification traces`);
    } catch (error) {
      console.error("Error fetching notification traces:", error);
      setError("Failed to fetch notification traces");
      toast.error("Failed to fetch notification traces");
    } finally {
      setIsLoading(false);
    }
  }, [fetchOrganizations]);

  // Load all traces on component mount
  useEffect(() => {
    fetchNotificationTraces();
  }, [fetchNotificationTraces]);

  const toggleTraceExpansion = (traceId) => {
    setExpandedTraces((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(traceId)) {
        newSet.delete(traceId);
      } else {
        newSet.add(traceId);
      }
      return newSet;
    });
  };

  const toggleHistoryItemExpansion = (itemKey) => {
    setExpandedHistoryItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const hasMetadata = (item) => {
    const standardFields = [
      "createdAt",
      "message",
      "documentCount",
      "notificationSent",
      "checklistFinished",
    ];
    return Object.keys(item).some((key) => !standardFields.includes(key));
  };

  const renderMetadataFields = (item) => {
    // Filter out standard fields to show only additional metadata
    const standardFields = [
      "createdAt",
      "message",
      "documentCount",
      "notificationSent",
      "checklistFinished",
    ];
    const metadataEntries = Object.entries(item).filter(
      ([key]) => !standardFields.includes(key)
    );

    if (metadataEntries.length === 0) return null;

    return (
      <div className="mt-2">
        <strong className="text-muted">Additional Metadata:</strong>
        <ul className="list-unstyled ms-3 mt-1">
          {metadataEntries.map(([key, value]) => (
            <li key={key}>
              <strong>{key}:</strong>{" "}
              {typeof value === "object"
                ? JSON.stringify(value, null, 2)
                : String(value)}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div
      className="container-fluid p-4 NotificationsPageContainer"
    >
      <style dangerouslySetInnerHTML={{ __html: accordionStyles }} />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <h1>
        <FaBell className="me-2" />
        Notification Traces
      </h1>

      {/* Instructions Section - Collapsible */}
      <div className="card mb-4">
        <div className="card-header">
          <h2 className="h5 mb-0">
            <button
              className="accordion-button collapsed p-2 text-decoration-none w-100 d-flex justify-content-between align-items-center border-0"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#instructionsCollapse"
              aria-expanded="false"
              aria-controls="instructionsCollapse"
            >
              <span>📖 How to View Notification Traces</span>
              <span className="accordion-arrow">▼</span>
            </button>
          </h2>
        </div>
        <div className="collapse" id="instructionsCollapse">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <h6>✅ How to Use:</h6>
                <ol>
                  <li>
                    All notification traces are automatically loaded when you
                    open this page
                  </li>
                  <li>Traces are sorted by creation date (newest first)</li>
                  <li>
                    Use the search box to filter by organization ID, loan ID, or
                    message content
                  </li>
                  <li>Click on any trace row to see the full history</li>
                  <li>
                    Each history entry shows the timestamp, message, and any
                    additional metadata
                  </li>
                </ol>
              </div>
              <div className="col-md-6">
                <h6>🔍 Search Options:</h6>
                <ul>
                  <li>Organization name or ID</li>
                  <li>Loan ID</li>
                  <li>Message content in history</li>
                </ul>
                <h6 className="mt-3">📊 Trace Information:</h6>
                <ul>
                  <li>
                    <strong>Organization:</strong> Shows organization name (with
                    ID below)
                  </li>
                  <li>
                    <strong>Loan ID:</strong> Click to open loan in Addy app
                    (new tab)
                  </li>
                  <li>
                    <strong>Documents:</strong> Number of documents processed
                  </li>
                  <li>
                    <strong>Notification:</strong> Whether notification was sent
                  </li>
                  <li>
                    <strong>Checklist:</strong> Processing checklist status
                  </li>
                </ul>
                <h6 className="mt-3">📋 History Items:</h6>
                <ul>
                  <li>Messages are displayed by default</li>
                  <li>Click "Details" to view additional metadata</li>
                  <li>Only items with metadata show the Details button</li>
                </ul>
              </div>
            </div>

            <div className="alert alert-info mt-3" role="alert">
              <strong>💡 Tip:</strong> Click on any trace row to expand and see
              the complete history of notifications for that trace.
            </div>
          </div>
        </div>
      </div>

      {/* Notification Traces List */}
      <div className="card mb-4">
        <div className="card-header">
          <h2 className="h5 mb-0">Notification Traces</h2>
          <small className="text-muted">View notification history</small>
          {isLoading && (
            <div className="mt-2">
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></div>
              <span className="text-muted">Loading notification traces...</span>
            </div>
          )}
          {traces.length > 0 && (
            <div className="mt-2">
              <span className="text-muted">
                Total traces: {traces.length}
                {searchTerm.trim() && (
                  <span> | Filtered: {filteredTraces.length}</span>
                )}
              </span>
            </div>
          )}
        </div>
        <div className="card-body">
          {/* Real-time search input for table */}
          {traces.length > 0 && (
            <div className="mb-3">
              <div className="row">
                <div className="col-md-6">
                  <label htmlFor="traceSearch" className="form-label">
                    Filter Traces
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      id="traceSearch"
                      placeholder="Filter by organization name/ID, loan ID, or message"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setSearchTerm("")}
                        title="Clear filter"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {searchTerm && (
                    <small className="text-muted">
                      Showing {filteredTraces.length} of {traces.length} traces
                    </small>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Traces Table with Accordion */}
          {filteredTraces.length > 0 && (
            <div>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th width="50"></th>
                      <th>Organization</th>
                      <th>Loan ID</th>
                      <th>Created At</th>
                      <th>Documents</th>
                      <th>Notification</th>
                      <th>Checklist</th>
                      <th>History Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTraces.map((trace, traceIndex) => {
                      const isExpanded = expandedTraces.has(trace.id);
                      const historyCount = trace.history?.length || 0;

                      // Debug: Log first trace in filtered results
                      if (traceIndex === 0) {
                        console.log("First filtered trace:", trace);
                        console.log(
                          "organizationId value:",
                          trace.organizationId,
                          "Type:",
                          typeof trace.organizationId
                        );
                        console.log(
                          "organizationName value:",
                          trace.organizationName
                        );
                        console.log(
                          "loanId value:",
                          trace.loanId,
                          "Type:",
                          typeof trace.loanId
                        );
                      }

                      return (
                        <React.Fragment key={trace.id}>
                          {/* Main Row - Clickable */}
                          <tr
                            className="cursor-pointer"
                            onClick={() => toggleTraceExpansion(trace.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              {isExpanded ? (
                                <FaChevronDown className="text-muted" />
                              ) : (
                                <FaChevronRight className="text-muted" />
                              )}
                            </td>
                            <td>
                              {trace.organizationName ? (
                                <div>
                                  <div>{trace.organizationName}</div>
                                  <small className="text-muted font-monospace">
                                    {trace.organizationId}
                                  </small>
                                </div>
                              ) : trace.organizationId ? (
                                <small className="font-monospace">
                                  {trace.organizationId}
                                </small>
                              ) : (
                                <span className="text-muted">N/A</span>
                              )}
                            </td>
                            <td>
                              {trace.loanId ? (
                                <a
                                  href={`https://app.addy.so/loans?tab=board&loan=${trace.loanId}&person=example%40example.com&tabValue=processingChecklist`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-decoration-none"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <small className="font-monospace text-primary">
                                    {trace.loanId}
                                  </small>
                                </a>
                              ) : (
                                <small className="text-muted">N/A</small>
                              )}
                            </td>
                            <td>
                              <FaClock className="me-1 text-muted" />
                              {formatTimestamp(trace.createdAt)}
                            </td>
                            <td>
                              <span className="badge bg-secondary">
                                {trace.documentCount ?? 0}
                              </span>
                            </td>
                            <td>
                              {trace.notificationSent ? (
                                <span className="badge bg-success">✓ Sent</span>
                              ) : (
                                <span className="badge bg-warning">
                                  ✗ Not Sent
                                </span>
                              )}
                            </td>
                            <td>
                              {trace.checklistFinished ? (
                                <span className="badge bg-success">
                                  ✓ Finished
                                </span>
                              ) : (
                                <span className="badge bg-warning">
                                  ✗ Pending
                                </span>
                              )}
                            </td>
                            <td>
                              <span className="badge bg-info">
                                {historyCount}{" "}
                                {historyCount === 1 ? "item" : "items"}
                              </span>
                            </td>
                          </tr>

                          {/* Expanded Row - History Details */}
                          {isExpanded && (
                            <tr>
                              <td colSpan="8" className="bg-light">
                                <div className="p-3">
                                  <h6 className="mb-3">
                                    <FaBell className="me-2" />
                                    Notification History
                                  </h6>

                                  {trace.history && trace.history.length > 0 ? (
                                    <div className="list-group">
                                      {trace.history.map(
                                        (historyItem, index) => {
                                          const itemKey = `${trace.id}-${index}`;
                                          const isItemExpanded =
                                            expandedHistoryItems.has(itemKey);
                                          const itemHasMetadata =
                                            hasMetadata(historyItem);

                                          return (
                                            <div
                                              key={index}
                                              className="list-group-item mb-2"
                                              style={{
                                                border: "1px solid #dee2e6",
                                                borderRadius: "4px",
                                              }}
                                            >
                                              <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                  <div className="d-flex align-items-start mb-1">
                                                    <small className="text-muted me-2">
                                                      <FaClock className="me-1" />
                                                      {formatTimestamp(
                                                        historyItem.createdAt
                                                      )}
                                                    </small>
                                                  </div>
                                                  <div className="mb-0">
                                                    {historyItem.message || (
                                                      <span className="text-muted">
                                                        No message
                                                      </span>
                                                    )}
                                                  </div>

                                                  {/* Expanded Metadata */}
                                                  {isItemExpanded &&
                                                    itemHasMetadata && (
                                                      <div className="mt-2 pt-2 border-top">
                                                        {renderMetadataFields(
                                                          historyItem
                                                        )}
                                                      </div>
                                                    )}
                                                </div>

                                                {/* Expand/Collapse Button */}
                                                {itemHasMetadata && (
                                                  <button
                                                    className="btn btn-sm btn-link text-decoration-none p-0 ms-2"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      toggleHistoryItemExpansion(
                                                        itemKey
                                                      );
                                                    }}
                                                    style={{
                                                      minWidth: "60px",
                                                    }}
                                                  >
                                                    {isItemExpanded ? (
                                                      <>
                                                        <FaChevronDown className="me-1" />
                                                        <small>Hide</small>
                                                      </>
                                                    ) : (
                                                      <>
                                                        <FaChevronRight className="me-1" />
                                                        <small>Details</small>
                                                      </>
                                                    )}
                                                  </button>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  ) : (
                                    <div
                                      className="alert alert-info"
                                      role="alert"
                                    >
                                      No history items found for this trace.
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {filteredTraces.length === 0 &&
            traces.length > 0 &&
            searchTerm.trim() && (
              <div className="alert alert-info" role="alert">
                <FaSearch className="me-2" />
                No traces found matching "{searchTerm}". Try a different search
                term.
              </div>
            )}

          {traces.length === 0 && hasLoaded && !isLoading && (
            <div className="alert alert-info" role="alert">
              No notification traces found.
            </div>
          )}

          {!hasLoaded && !isLoading && (
            <div className="alert alert-info" role="alert">
              Loading notification traces...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
